import React, { useEffect, useState } from 'react';
import Div from '@/shared/Div';
import Button from '@/shared/Button';
import Dropdown from '@/shared/crisp/Dropdown';
import Divider from '@/shared/Divider';
import { APIProvider, Map, Marker, MapProps } from '@vis.gl/react-google-maps';
import { getEnvValue } from '@/utils/envUtils';
import ListMap from '@/components/Map';
import classNames from 'classnames';
import Heading from '@/shared/Heading';
import Link from '@/shared/Link';
import { getAuthorizedCenterURL } from '@/utils/urls';
import dlv from 'dlv';

const API_KEY = getEnvValue('GOOGLE_MAP_API_KEY');

interface Location {
  id: number;
  attributes: {
    locationId: string;
    name: string;
    address: string | null;
    pincode: string;
    salePhone: string | null;
    servicePhone: string | null;
    latitude: number;
    longitude: number;
  };
}

interface State {
  id: string;
  label: string;
}

interface ServiceLocationsSelectorProps {
  states: State[];
  cities: State[];
  selectedState: State | null;
  showLocations: boolean;
  locations: Location[];
  isMobile: boolean;
  isTablet: boolean;
  categoryId: string;
  isAuthorizedCenter?: boolean;
  onStateChange: (state: State) => void;
  onCityChange: (city: State) => void;
  onReset: () => void;
  onSubmit: () => void;
  className?: string;
  googleMapApiKey: string;
}

const ServiceLocationsSelector: React.FC<ServiceLocationsSelectorProps> = ({
  states,
  cities,
  selectedState,
  categoryId,
  showLocations,
  locations = [],
  isMobile,
  isTablet,
  isAuthorizedCenter = false,
  onStateChange,
  onCityChange,
  onReset,
  onSubmit,
  className = '',
  googleMapApiKey,
}) => {
  const [stateKey, setStateKey] = useState(0);
  const [cityKey, setCityKey] = useState(0);
  const [mapKey, setMapKey] = useState(0);
  const [mapView, setMapView] = useState<{
    center: { lat: number; lng: number };
    zoom: number;
  }>({
    center: { lat: 20.5937, lng: 78.9629 },
    zoom: 5,
  });

  const calculateMapCenter = (locationList: Location[]) => {
    if (locationList.length === 0) {
      return { lat: 20.5937, lng: 78.9629 };
    }

    const sumLat = locationList.reduce((sum, loc) => sum + loc.attributes.latitude, 0);
    const sumLng = locationList.reduce((sum, loc) => sum + loc.attributes.longitude, 0);

    return {
      lat: sumLat / locationList.length,
      lng: sumLng / locationList.length,
    };
  };

  const calculateZoomLevel = (locationList: Location[]) => {
    if (locationList.length === 0) return 5;
    if (locationList.length === 1) return 12;

    let maxDistance = 0;

    for (let i = 0; i < locationList.length; i++) {
      for (let j = i + 1; j < locationList.length; j++) {
        const lat1 = locationList[i].attributes.latitude;
        const lng1 = locationList[i].attributes.longitude;
        const lat2 = locationList[j].attributes.latitude;
        const lng2 = locationList[j].attributes.longitude;

        const R = 6371;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLng = ((lng2 - lng1) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;

        maxDistance = Math.max(maxDistance, distance);
      }
    }

    if (maxDistance <= 5) return 12;
    else if (maxDistance <= 20) return 10;
    else if (maxDistance <= 50) return 9;
    else if (maxDistance <= 100) return 8;
    else if (maxDistance <= 200) return 7;
    else if (maxDistance <= 500) return 6;
    else return 5;
  };

  useEffect(() => {
    setMapView({
      center: calculateMapCenter(locations),
      zoom: calculateZoomLevel(locations),
    });
  }, [locations]);

  const handleMapViewChange: MapProps['onCameraChanged'] = (event) => {
    setMapView({
      center: event.detail.center,
      zoom: event.detail.zoom,
    });
  };
  useEffect(() => {
    setCityKey((prev) => prev + 1);
  }, [cities]);

  const handleReset = () => {
    setStateKey((prev) => prev + 1);
    setCityKey((prev) => prev + 1);
    setMapKey((prev) => prev + 1);

    setMapView({
      center: { lat: 20.5937, lng: 78.9629 },
      zoom: 5,
    });

    onReset();
  };

  const isDefaultSelection = selectedState?.id === 'all' && cities[0]?.id === 'all';

  const renderSelectors = () => (
    <Div className="flex flex-col self-stretch items-stretch gap-s5 w-full">
      <Dropdown
        key={`state-${stateKey}`}
        name="states"
        dropdownList={states}
        onChange={onStateChange}
      />
      <Dropdown
        key={`city-${cityKey}`}
        name="cities"
        dropdownList={cities}
        onChange={onCityChange}
      />
    </Div>
  );

  const renderButtons = () => (
    <Div className="flex flex-row gap-s5 mt-s5 w-full">
      <Button
        theme="secondary"
        size="base"
        onClick={handleReset}
        className={classNames('flex-1', {
          'min-w-[120px]': isMobile,
          'min-w-[180px]': !isMobile,
        })}
        disabled={isDefaultSelection}
      >
        Reset
      </Button>
      <Button
        theme="primary"
        size="base"
        onClick={onSubmit}
        className={classNames('flex-1', {
          'min-w-[120px]': isMobile,
          'min-w-[180px]': !isMobile,
        })}
        disabled={isDefaultSelection}
      >
        Submit
      </Button>
    </Div>
  );
  const renderLocationCard = (location: Location) => {
    const { name, address, pincode, salePhone, servicePhone, locationId } = location.attributes;
    const { cityId } = dlv(location, 'attributes.city.data.attributes', {});
    const content = (
      <Div className="flex flex-col items-start gap-s4 w-full">
        <Div className="text-body-lg text-neutral-900">{name || 'Address not available'}</Div>
        <Div className="text-body-lg font-bold text-neutral-900">
          {`${address || ''} ${
            location.attributes.address && location.attributes.pincode ? '-' : ''
          } ${pincode || ''}`}
        </Div>
        {servicePhone && (
          <Div className="text-body-base text-neutral-700">
            Service Request :{' '}
            <Link
              href={`tel:${servicePhone}`}
              className="text-cm-bold"
              onClick={(e) => e.stopPropagation()}
            >
              {servicePhone}
            </Link>
          </Div>
        )}
        {salePhone && (
          <Div className="text-body-base text-neutral-700">
            Sales Enquiry :{' '}
            <Link
              href={`tel:${salePhone}`}
              className="text-cm-bold"
              onClick={(e) => e.stopPropagation()}
            >
              {salePhone}
            </Link>
          </Div>
        )}
      </Div>
    );

    if (isAuthorizedCenter) {
      return (
        <Link
          href={getAuthorizedCenterURL(cityId, pincode, location.id)}
          className="w-full"
          key={locationId}
        >
          <Div className="rounded-cxs bg-cs-primary p-s6 hover:shadow-medium transition-shadow w-full">
            {content}
          </Div>
        </Link>
      );
    }
    return (
      <Div key={locationId} className="w-full">
        {content}
        <Divider className="w-full my-s4" />
      </Div>
    );
  };

  const renderMap = () => {
    const mapSizes = {
      mobile: 'w-full h-[350px]',
      tablet: 'w-full h-[450px]',
      desktop: 'w-full h-[600px] lg:sticky lg:top-24',
    };

    const mapSize = isMobile ? mapSizes.mobile : isTablet ? mapSizes.tablet : mapSizes.desktop;

    return (
      <Div className={classNames('relative rounded-cs overflow-hidden', mapSize)}>
        <APIProvider apiKey={googleMapApiKey}>
          <Map
            key={`map-${mapKey}`}
            zoom={mapView.zoom}
            center={mapView.center}
            onCameraChanged={handleMapViewChange}
            streetViewControl={false}
            zoomControl={false}
            fullscreenControl={false}
            mapTypeControl={false}
            disableDoubleClickZoom={false}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
            }}
          >
            {locations.map((location) => (
              <Marker
                key={location.attributes.locationId}
                position={{
                  lat: location.attributes.latitude,
                  lng: location.attributes.longitude,
                }}
                title={location.attributes.name}
              />
            ))}
          </Map>
        </APIProvider>
      </Div>
    );
  };

  const renderLocations = () => {
    if (!showLocations) return null;
    const locationsHeader = (
      <Heading level={3} className="text-h-3 text-cti-primary">
        {locations.length} Locations Found
      </Heading>
    );

    if (isMobile || isTablet) {
      if (locations.length === 0) {
        return <Div className="flex flex-col gap-s6">{locationsHeader}</Div>;
      }

      return (
        <Div className="flex flex-col gap-s6 w-full">
          {locationsHeader}

          <ListMap list={locations} render={renderLocationCard} />
        </Div>
      );
    }

    if (locations.length === 0) {
      return <Div className="flex flex-col items-start gap-s6">{locationsHeader}</Div>;
    }

    return (
      <Div className="flex flex-col items-start gap-s6">
        {locationsHeader}
        <Div
          className={classNames('flex flex-col', {
            'gap-s4': isAuthorizedCenter,
            'gap-0': !isAuthorizedCenter,
          })}
        >
          <ListMap list={locations} render={renderLocationCard} />
        </Div>
      </Div>
    );
  };
  return (
    <Div
      className={classNames('flex min-h-[500px] mb-16', className, {
        'flex-col w-full gap-s6': isMobile || isTablet,
        'flex-col lg:flex-row items-start w-full max-w-[1240px] gap-s8': !isMobile && !isTablet,
      })}
    >
      <Div
        className={classNames('flex flex-col items-start gap-s8', {
          'w-full': isMobile || isTablet,
          'w-full lg:w-[374px]': !isMobile && !isTablet,
        })}
      >
        <Div
          className={classNames('flex flex-col items-start gap-s8 w-full', {
            'mb-8': showLocations,
          })}
        >
          <Div className="flex flex-col items-start gap-s6 w-full">
            <Heading level={3} className="text-h-3 font-bold">
              Select State and City
            </Heading>
            {renderSelectors()}
            {renderButtons()}
          </Div>
          <Div className="w-full">{renderLocations()}</Div>
        </Div>
      </Div>
      {locations.length > 0 && (
        <Div className="w-full lg:sticky lg:top-35 lg:flex-1">{renderMap()}</Div>
      )}
    </Div>
  );
};

export default ServiceLocationsSelector;
